import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

import FormattedText from "../../../FormattedText/FormattedText";
import SelectCountryModalSheet from "../../../SelectCountryModal/SelectCountryModalSheetV2";
import {
  Country,
  CountryListField,
  CountryListItem,
  TextNodeType,
} from "../../CommonLandingMweb.types";
import { COMMON_LANDING_CONSTANTS } from "../../constants";
import { Container, DisplayText, ErrorText, Label, SubtitleSpan } from "./Styles";

interface CountrySelectorProps {
  countryList?: CountryListField;
  onCountryChange?: (countries: Country[]) => void;
  initialSelectedCountries?: Country[];
  errorText?: string;
  /** Portaled sheet z-index; default 10 (see SelectCountryModalSheet). */
  zIndex?: number;
  /** When true, the field is display-only (no country sheet). */
  disabled?: boolean;
  /**
   * When `disabled` is true and this string is non-empty, shown as the main value instead of
   * resolving names from `initialSelectedCountries` (e.g. API `displayData` without `countryList`).
   */
  readOnlyDisplayText?: string;
}

function normalizeKeywords(
  keywords: CountryListItem["keywords"]
): string | string[] | undefined {
  if (keywords == null) return undefined;
  if (Array.isArray(keywords)) return keywords;
  if (typeof keywords === "string") {
    const parts = keywords
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts : undefined;
  }
  return undefined;
}

/** Build modal rows + selection / disabled flags (parity with insurance `TravellingTo` hydration). */
function buildCountryDataForModal(
  value: CountryListItem[] | undefined,
  selectedCodes: string[]
): any[] {
  if (!value?.length) return [];
  let rows = value.map((row) => ({
    ...row,
    keywords: normalizeKeywords(row.keywords) ?? row.keywords,
    isSelected: selectedCodes.includes(row.code),
    isDisabled: false,
  }));

  const isExclusiveCountrySelected = rows.find(
    (item: any) => item.isExclusiveSelection && item.isSelected
  );
  rows = rows.map((country: any) => {
    if (isExclusiveCountrySelected) {
      return country.isExclusiveSelection
        ? { ...country }
        : { ...country, isSelected: false, isDisabled: true };
    }
    if (rows.some((item: any) => item.isSelected)) {
      return country.isExclusiveSelection
        ? { ...country, isDisabled: true }
        : { ...country };
    }
    return { ...country };
  });

  const selectedRegionCodes = rows
    .filter((data: any) => data.isSelected && data.type === "REGION")
    .map((data: any) => data.code);

  selectedRegionCodes.forEach((code: string) => {
    rows = rows.map((country: any) => {
      if (country.regionCodes?.includes(code)) {
        return { ...country, isDisabled: true };
      }
      return { ...country };
    });
  });

  return rows;
}

function popularCountriesOrdering(rows: any[]): any[] {
  let popular =
    rows?.filter((country: any) => country.isSelected || country.isPopular) ??
    [];
  return [
    ...popular.filter(
      (country: any) => country.isSelected && !country.isDisabled
    ),
    ...popular.filter((country: any) => country.isDisabled),
    ...popular.filter(
      (country: any) => !country.isSelected && !country.isDisabled
    ),
  ];
}

function rowsToSelectedCountries(rows: any[]): Country[] {
  return rows
    .filter(
      (c: any) =>
        c.isSelected && !c.isDisabled && c.type !== "HEADER" && c.code && c.name
    )
    .map((c: any) => ({
      code: c.code,
      name: c.name,
      subtitle: c.subtitle,
    }));
}

export const CountrySelector = ({
  countryList,
  onCountryChange,
  initialSelectedCountries = [],
  errorText,
  zIndex,
  disabled = false,
  readOnlyDisplayText,
}: CountrySelectorProps) => {
  const [selected, setSelected] = useState<Country[]>(initialSelectedCountries);
  const [showModal, setShowModal] = useState(false);
  const [countryData, setCountryData] = useState<any[]>([]);
  const [showCountry, setShowCountry] = useState<any[]>([]);
  const [popularCountries, setPopularCountries] = useState<any[]>([]);
  const [, setIsCountryError] = useState(false);
  const wasModalOpen = useRef(false);

  /** Avoid resetting local selection every parent render when prop is a new array with the same codes. */
  const initialSelectedCodesKey = (initialSelectedCountries ?? [])
    .map((c) => c.code)
    .filter(Boolean)
    .sort()
    .join(",");

  useEffect(() => {
    setSelected(initialSelectedCountries || []);
    /* Sync when parent selected codes change, not when array identity changes (see initialSelectedCodesKey). */
  }, [initialSelectedCodesKey]); // eslint-disable-line react-hooks/exhaustive-deps -- initialSelectedCountries read intentionally

  const placeHolder =
    countryList?.placeHolder || COMMON_LANDING_CONSTANTS.DEFAULT_SUBTITLE;

  const display = useMemo(() => {
    const readOnly =
      disabled &&
      readOnlyDisplayText != null &&
      readOnlyDisplayText.trim() !== "";
    if (readOnly) {
      return readOnlyDisplayText.trim();
    }
    if (!selected.length) return placeHolder;

    const hasSubtitle = selected.length === 1 && selected[0].subtitle;
    if (hasSubtitle) {
      return (
        <>
          {selected[0].name}
          <SubtitleSpan>, {selected[0].subtitle}</SubtitleSpan>
        </>
      );
    }

    return selected.map((country) => country.name).join(", ");
  }, [disabled, readOnlyDisplayText, selected, placeHolder]);

  const openModal = useCallback(() => {
    const selectedCodes = selected.map((c) => c.code);
    const next = buildCountryDataForModal(countryList?.value, selectedCodes);
    setCountryData(next);
    const popular = popularCountriesOrdering(next);
    setShowCountry([...popular]);
    setPopularCountries([...popular]);
    setShowModal(true);
  }, [countryList?.value, selected]);

  const handleSelectCountry = useCallback(() => {
    setShowModal(false);
  }, []);

  useEffect(() => {
    if (wasModalOpen.current && !showModal) {
      const next = rowsToSelectedCountries(countryData);
      setSelected(next);
      onCountryChange?.(next);
    }
    wasModalOpen.current = showModal;
  }, [showModal, countryData, onCountryChange]);

  const handleDescriptionModal = useCallback((country: any) => {
    return country?.description ?? "";
  }, []);

  const label: TextNodeType | string = countryList?.label ?? "Destination";

  return (
    <div>
      <Container
        type="button"
        onClick={disabled ? undefined : openModal}
        disabled={disabled}
        aria-disabled={disabled}
        style={disabled ? { opacity: 0.65, cursor: "not-allowed" } : undefined}
      >
        <Label>
          <FormattedText value={label} />
        </Label>
        <DisplayText>{display}</DisplayText>
      </Container>
      {errorText ? <ErrorText>{errorText}</ErrorText> : null}
      {showModal ? (
        <SelectCountryModalSheet
          countryData={countryData}
          showCountry={showCountry}
          handleSelectCountry={handleSelectCountry}
          setCountryData={setCountryData}
          setShowCountry={
            setShowCountry as Dispatch<SetStateAction<any[] | undefined>>
          }
          setIsCountryError={setIsCountryError}
          popularCountries={popularCountries}
          setPopularCountries={setPopularCountries}
          customSubTitle={placeHolder}
          countrySelectErrMsg={
            countryList?.countrySelectErrMsg ||
            countryList?.error_message ||
            "Please adjust your selection"
          }
          handleDescriptionModal={handleDescriptionModal}
          zIndex={zIndex}
        />
      ) : null}
    </div>
  );
};
